import React, { useState, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { getCsrfToken } from '../utils/auth';
import '../styles/AiDisclaimerModal.css';

const ORCANOS_TERMS_URL = 'https://www.orcanos.com/orcanos-terms-of-use';

/**
 * AI Usage Disclaimer — ported from Orcanos QMS ("Ask Paul"). The text itself is NOT
 * hardcoded here — it comes from the backend (`GET /api/ai-disclaimer/status` →
 * `current_markdown`, see AI_DISCLAIMER_BODY_MARKDOWN in ai_disclaimer_api.py) so there is
 * exactly one place that owns the wording, and every acceptance snapshots the exact text it
 * applied to.
 *
 * Two modes on the same component:
 *  - mode="gate": blocking — no close button, no backdrop dismiss. Rendered by App.jsx in
 *    place of the whole app when `aiEnabled` is true and the tenant has not accepted the
 *    current disclaimer (see ai_disclaimer_api._status_for — this also re-triggers whenever
 *    AI is turned off then back on, even if nothing about the text changed). The "I Agree"
 *    checkbox stays disabled until the body is scrolled to the end.
 *  - mode="view": a normal dismissable modal, opened from the user menu ("AI Disclaimer") so
 *    anyone can re-read it later. Shows the full acceptance history. No re-acceptance is
 *    collected here.
 */
export default function AiDisclaimerModal({ mode = 'gate', markdown, history, onAccepted, onClose }) {
  const [scrolledToBottom, setScrolledToBottom] = useState(mode === 'view');
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const bodyRef = useRef(null);

  const handleScroll = () => {
    if (mode !== 'gate') return;
    const el = bodyRef.current;
    if (!el) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 24) {
      setScrolledToBottom(true);
    }
  };

  const handleAccept = async () => {
    if (!agreeChecked || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const resp = await fetch('/api/ai-disclaimer/accept', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': getCsrfToken() },
      });
      let data = null;
      try { data = await resp.json(); } catch { /* handled below */ }
      if (!resp.ok) {
        throw new Error((data && data.detail) || `Could not record your acceptance (HTTP ${resp.status}).`);
      }
      onAccepted(data);
    } catch (e) {
      setError(e.message || 'Could not record your acceptance. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const overlayProps = mode === 'view'
    ? { onMouseDown: (e) => { if (e.target === e.currentTarget) onClose(); } }
    : {};

  return (
    <div
      className={`ai-disclaimer-overlay ${mode === 'gate' ? 'ai-disclaimer-overlay--gate' : ''}`}
      role="dialog"
      aria-modal="true"
      {...overlayProps}
    >
      <div className="ai-disclaimer-card" onMouseDown={e => e.stopPropagation()}>
        <div className="ai-disclaimer-header">
          <div>
            <h2>AI Usage Disclaimer</h2>
            <p>
              {mode === 'gate'
                ? 'Please read this disclaimer in full before using AI features.'
                : 'Orcanos AI Usage Disclaimer'}
            </p>
          </div>
          {mode === 'view' && (
            <button className="ai-disclaimer-close" onClick={onClose} aria-label="Close">✕</button>
          )}
        </div>

        <div className="ai-disclaimer-body" ref={bodyRef} onScroll={handleScroll}>
          <div className="ai-disclaimer-markdown">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown || ''}</ReactMarkdown>
          </div>

          {mode === 'view' && history?.length > 0 && (
            <div className="ai-disclaimer-acceptance-note">
              <strong>Acceptance history for this account</strong>
              <ul>
                {history.map((h, i) => (
                  <li key={i}>
                    Version {h.version} accepted by <strong>{h.accepted_by || 'unknown user'}</strong> on{' '}
                    {h.accepted_at ? new Date(h.accepted_at.replace(' ', 'T') + 'Z').toLocaleString() : 'unknown date'}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {mode === 'gate' && (
            <p className="ai-disclaimer-scroll-hint">
              {scrolledToBottom
                ? 'You have reached the end of this disclaimer.'
                : 'Scroll to the end of this disclaimer to enable the checkbox below.'}
            </p>
          )}
        </div>

        <div className="ai-disclaimer-footer">
          {mode === 'gate' && (
            <>
              <label className={`ai-disclaimer-checkbox ${!scrolledToBottom ? 'ai-disclaimer-checkbox--disabled' : ''}`}>
                <input
                  type="checkbox"
                  checked={agreeChecked}
                  disabled={!scrolledToBottom}
                  onChange={e => setAgreeChecked(e.target.checked)}
                />
                <span>
                  I have read and understood this AI Usage Disclaimer, and I agree to it — on behalf of
                  myself and my organization — including the incorporated{' '}
                  <a href={ORCANOS_TERMS_URL} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()}>
                    Orcanos Terms of Use
                  </a>.
                </span>
              </label>
              {error && <div className="ai-disclaimer-error">{error}</div>}
              <button
                className="ai-disclaimer-btn ai-disclaimer-btn--primary ai-disclaimer-accept-btn"
                disabled={!agreeChecked || submitting}
                onClick={handleAccept}
              >
                {submitting ? 'Saving…' : 'I Agree & Continue'}
              </button>
            </>
          )}
          {mode === 'view' && (
            <button className="ai-disclaimer-btn ai-disclaimer-btn--secondary ai-disclaimer-close-btn" onClick={onClose}>
              Close
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
